import {useMemo} from "react";
import {useQuery} from "@tanstack/react-query";

import {ParamsOptions} from "@/src/modules/core/types";

import {mergePropertiesById} from "../lib/merge-properties-by-id";
import {propertiesService} from "../services";
import {useInfiniteProperties} from "./use-infinite-properties";

const DISPATCH_PROPERTIES_LIMIT = 100;

type UseInfinitePropertiesWithDispatchOptions = {
  /** Include dispatch sites (e.g. hidden Dispatch Center) in the merged list. */
  mergeDispatchSites?: boolean;
};

/**
 * Property combobox / lists scoped to managed sites, optionally merged with
 * all dispatch sites. Matches schedule grid property sourcing.
 */
export function useInfinitePropertiesWithDispatch(
  options: ParamsOptions,
  {mergeDispatchSites = false}: UseInfinitePropertiesWithDispatchOptions = {},
) {
  const sitesQuery = useInfiniteProperties(options);

  const dispatchFilter = useMemo(() => {
    const base: Record<string, unknown> = {status: 1, is_dispatch: 1};
    const name = options.filter?.name;
    if (name) {
      base.name = name;
    }
    return base;
  }, [options.filter?.name]);

  const dispatchQuery = useQuery({
    queryKey: ["infinite-properties-dispatch-sites", dispatchFilter, options.sort],
    enabled: mergeDispatchSites,
    queryFn: () =>
      propertiesService.getProperties({
        sort: options.sort ?? "name",
        filter: dispatchFilter,
        page: {limit: DISPATCH_PROPERTIES_LIMIT, offset: 0},
      }),
  });

  const properties = useMemo(() => {
    if (!mergeDispatchSites) {
      return sitesQuery.properties;
    }
    return mergePropertiesById(sitesQuery.properties, dispatchQuery.data?.data ?? []);
  }, [mergeDispatchSites, sitesQuery.properties, dispatchQuery.data?.data]);

  return {
    ...sitesQuery,
    properties,
    isFetching:
      sitesQuery.isFetching || (mergeDispatchSites ? dispatchQuery.isFetching : false),
    isPending: sitesQuery.isPending || (mergeDispatchSites ? dispatchQuery.isPending : false),
    isError: sitesQuery.isError || (mergeDispatchSites ? dispatchQuery.isError : false),
    error: sitesQuery.error ?? (mergeDispatchSites ? dispatchQuery.error : undefined),
  };
}
